/*
 * Custom Message card popup (snippets/card-message-dialog.liquid).
 * - Opens from [data-card-msg-open] on Custom Message cart lines.
 * - Live preview of the inside right panel (4.25" x 5.5"), text auto-shrinks to fit (16pt -> 9pt).
 * - Saves To / Message / From / Font as line item properties. A line with quantity > 1 is split
 *   so every card gets its own message.
 * - Checkout guard: if any Custom Message is still blank ([data-card-msg-missing]), checkout
 *   (and Proceed Anyway) opens the popup for the first blank message instead.
 */
(() => {
  const dialog = document.getElementById('card-message-dialog');
  if (!dialog) return;
  const form = dialog.querySelector('[data-card-msg-form]');
  const fields = { to: form.elements.to, message: form.elements.message, from: form.elements.from };
  const preview = dialog.querySelector('[data-card-msg-preview]');
  const pTo = preview.querySelector('[data-preview-to]');
  const pMessage = preview.querySelector('[data-preview-message]');
  const pFrom = preview.querySelector('[data-preview-from]');
  const count = dialog.querySelector('[data-card-msg-count]');
  const errorEl = dialog.querySelector('[data-card-msg-error]');
  const saveBtn = dialog.querySelector('[data-card-msg-save]');
  const fontButtons = [...dialog.querySelectorAll('.cm-fonts [data-font]')];
  const MAX = 300;
  const PT_PER_PANEL_WIDTH = 4.25 * 72; // panel width in points
  const placeholderText = fields.message.getAttribute('placeholder') || '';
  let current = null; // { key, variantId, quantity }
  const picker = document.getElementById('card-picker-dialog');
  let pickerCartId = null;
  let reopenPicker = false; // message popup was opened from the card picker
  let font = 'Script';

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const boxField = dialog.querySelector('[data-card-msg-box-field]');
  const boxSelect = boxField ? boxField.querySelector('select') : null;
  // Gift box names in the cart, in cart order. Keep in step with snippets/cart-box-labels.liquid.
  function boxLabels(items) {
    const out = [];
    const plain = { 3: 0, 6: 0 };
    items.forEach((it) => {
      const n = it.handle === 'small-gift-box' ? 3 : it.handle === 'large-gift-box' ? 6 : 0;
      const nbg = !n && noBoxGroupStart(it, items);
      if (nbg) { out.push(nbg.label); return; }
      // A set grouped without a box (it carries a card) counts as a gift destination too.
      if (!n && it.properties?._sg && !items.some((b) => b !== it && b.properties?._sg === it.properties._sg)) { out.push(it.properties['Gift Set']); return; }
      if (!n || it.properties?._slot != null) return;
      if (it.properties?._sg) out.push(it.properties['Gift Set']);
      else if (it.properties?._bb) out.push(`${it.properties['Custom Box']} · Custom ${n}-Pack`);
      else for (let q = 0; q < it.quantity; q++) out.push(`${n}-Pack Gift Box ${++plain[n]}`);
    });
    return out;
  }
  // Fill "Put this card in" with the cart's gift boxes; boxes another card already uses are marked and disabled.
  async function fillBoxes(lineKey, chosen) {
    if (!boxSelect) return;
    boxField.hidden = true;
    let items = [];
    try { items = (await (await fetch(root() + 'cart.js', { credentials: 'same-origin' })).json()).items || []; } catch (e) { return; }
    const labels = boxLabels(items);
    const taken = new Set(items.filter((it) => it.handle === 'custom-message' && it.key !== lineKey && it.properties?.['For Box']).map((it) => it.properties['For Box']));
    const anyText = boxSelect.options[0]?.textContent || 'Any box';
    const takenText = boxField.dataset.takenText || '(already has a card)';
    boxSelect.innerHTML = '';
    boxSelect.append(new Option(anyText, ''));
    labels.forEach((label) => {
      const isTaken = taken.has(label) && label !== chosen;
      const o = new Option(isTaken ? `${label} ${takenText}` : label, label);
      o.disabled = isTaken;
      boxSelect.append(o);
    });
    boxSelect.value = labels.includes(chosen) ? chosen : '';
    // Merchant rule: only ask which box when the order has 2+ gift boxes AND 2+ Greeting Cards.
    const cardCount = items.filter((it) => it.handle === 'greeting-card').reduce((a, it) => a + it.quantity, 0);
    boxField.hidden = labels.length < 2 || cardCount < 2;
    bpRender(items, labels, taken, anyText, takenText);
  }

  // An edited set without a gift box: _bb jars and no box line. Counted once, at its first jar.
  const isBoxH = (h) => h === 'small-gift-box' || h === 'large-gift-box';
  function noBoxGroupStart(it, items) {
    const id = it.properties?._bb;
    if (!id || isBoxH(it.handle)) return null;
    const lines = items.filter((j) => j.properties?._bb === id);
    if (lines.some((j) => isBoxH(j.handle)) || lines[0] !== it) return null;
    const jars = lines.reduce((a, j) => a + j.quantity, 0);
    const size = lines.some((j) => j.properties?._bbn === '6') || jars > 3 ? 6 : 3;
    const name = String(it.properties['Custom Box'] || '').split(' · ')[0];
    return { label: `${name} · Custom ${size}-Pack`, lines };
  }
  // ---------- "Put this card in" bp: each box with the spices inside it ----------
  // Same order and labels as boxLabels(); custom boxes list their jars by slot, plain boxes say none are arranged.
  function bpContents(items) {
    const out = [];
    items.forEach((it) => {
      const n = it.handle === 'small-gift-box' ? 3 : it.handle === 'large-gift-box' ? 6 : 0;
      const nbg = !n && noBoxGroupStart(it, items);
      if (nbg) {
        out.push({ jars: nbg.lines.filter((j) => j.properties?._slot != null).sort((a, b) => Number(a.properties._slot) - Number(b.properties._slot)).map((j) => ({ slot: j.properties._slot, title: j.product_title, image: j.image })), plain: false });
        return;
      }
      if (!n && it.properties?._sg && !items.some((b) => b !== it && b.properties?._sg === it.properties._sg)) { out.push({ jars: [{ slot: '', title: it.product_title, image: it.image }], plain: false }); return; }
      if (!n || it.properties?._slot != null) return;
      if (it.properties?._sg) {
        // Set + box: the set's name stands in for the jar list.
        const set = items.find((j) => j.properties?._sg === it.properties._sg && j.key !== it.key);
        out.push({ jars: set ? [{ slot: '', title: set.product_title, image: set.image }] : [], plain: false });
      } else if (it.properties?._bb) {
        const id = it.properties._bb;
        const jars = items
          .filter((j) => j.properties?._bb === id && j.properties?._slot != null)
          .sort((a, b) => Number(a.properties._slot) - Number(b.properties._slot))
          .map((j) => ({ slot: j.properties._slot, title: j.product_title, image: j.image }));
        out.push({ jars, plain: false });
      } else {
        for (let q = 0; q < it.quantity; q++) out.push({ jars: [], plain: true });
      }
    });
    return out;
  }
  const bp = boxField ? boxField.querySelector('[data-cm-bp]') : null;
  const bpBtn = bp ? bp.querySelector('[data-cm-bp-btn]') : null;
  const bpList = bp ? bp.querySelector('[data-cm-bp-list]') : null;
  let bpData = [];
  const bpEsc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const bpThumb = (url) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=60' : '');
  function bpShow() {
    if (!bpBtn) return;
    const entry = bpData.find((d) => d.value === boxSelect.value) || bpData[0];
    bpBtn.querySelector('[data-cm-bp-name]').textContent = entry ? entry.name : '';
    bpBtn.querySelector('[data-cm-bp-jars]').textContent = entry && entry.jars.length ? entry.jars.map((j) => j.title).join(', ') : '';
    bpList.querySelectorAll('[role="option"]').forEach((li) => li.setAttribute('aria-selected', String(li.dataset.value === boxSelect.value)));
  }
  function bpRender(items, labels, taken, anyText, takenText) {
    if (!bp) return;
    const contents = bpContents(items);
    const plainText = bp.dataset.plainText || '';
    bpData = [{ value: '', name: anyText, jars: [], plain: false, disabled: false }].concat(
      labels.map((label, i) => ({ value: label, name: label, jars: contents[i]?.jars || [], plain: !!contents[i]?.plain, disabled: taken.has(label) && label !== boxSelect.value })),
    );
    bpList.innerHTML = bpData.map((d, i) => {
      const jars = d.jars.length
        ? `<ul class="cm-bp-jars">${d.jars.map((j) => `<li><span class="cm-bp-num">${bpEsc(j.slot)}</span>${j.image ? `<img src="${bpEsc(bpThumb(j.image))}" alt="">` : ''}<span>${bpEsc(j.title)}</span></li>`).join('')}</ul>`
        : d.plain ? `<p class="cm-bp-plain">${bpEsc(plainText)}</p>` : '';
      return `<li class="cm-bp-opt" role="option" id="cm-bp-${i}" data-value="${bpEsc(d.value)}" aria-selected="false"${d.disabled ? ' aria-disabled="true"' : ''}><span class="cm-bp-opt-name">${bpEsc(d.name)}</span>${d.disabled ? `<span class="cm-bp-opt-taken">${bpEsc(takenText)}</span>` : ''}${jars}</li>`;
    }).join('');
    bpClose();
    bpShow();
  }
  function bpOpen() {
    bpList.hidden = false;
    bpBtn.setAttribute('aria-expanded', 'true');
    (bpList.querySelector('[aria-selected="true"]') || bpList.firstElementChild)?.scrollIntoView({ block: 'nearest' });
    requestAnimationFrame(() => bpList.scrollIntoView({ block: 'nearest', behavior: 'smooth' })); // keep the whole list visible inside the popup
  }
  function bpClose() {
    if (!bpList) return;
    bpList.hidden = true;
    bpBtn.setAttribute('aria-expanded', 'false');
  }
  function bpChoose(li) {
    if (!li || li.getAttribute('aria-disabled') === 'true') return;
    boxSelect.value = li.dataset.value;
    boxSelect.dispatchEvent(new Event('change', { bubbles: true }));
    bpShow();
    bpClose();
    bpBtn.focus();
  }
  if (bp) {
    bpBtn.addEventListener('click', () => (bpList.hidden ? bpOpen() : bpClose()));
    bpList.addEventListener('click', (e) => bpChoose(e.target.closest('[role="option"]')));
    dialog.addEventListener('click', (e) => { if (!bpList.hidden && !bp.contains(e.target)) bpClose(); });
    bp.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !bpList.hidden) { e.preventDefault(); e.stopPropagation(); bpClose(); bpBtn.focus(); return; }
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) return;
      if (bpList.hidden) { if (e.target === bpBtn && e.key !== 'Enter' && e.key !== ' ') { e.preventDefault(); bpOpen(); } return; }
      const opts = [...bpList.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];
      let i = opts.findIndex((o) => o.classList.contains('is-active'));
      if (i < 0) i = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (opts[i]) bpChoose(opts[i]); return; }
      e.preventDefault();
      i = Math.max(0, Math.min(opts.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)));
      opts.forEach((o) => o.classList.remove('is-active'));
      opts[i]?.classList.add('is-active');
      opts[i]?.scrollIntoView({ block: 'nearest' });
      bpBtn.setAttribute('aria-activedescendant', opts[i]?.id || '');
    });
  }

  function setFont(name) {
    font = ['Script', 'Classic', 'Modern'].includes(name) ? name : 'Script';
    fontButtons.forEach((b) => b.setAttribute('aria-checked', String(b.dataset.font === font)));
    preview.classList.remove('cm-font-script', 'cm-font-classic', 'cm-font-modern');
    preview.classList.add('cm-font-' + font.toLowerCase());
    fit();
  }

  function fit() {
    const width = preview.parentElement.clientWidth;
    if (!width) return;
    const ptToPx = width / PT_PER_PANEL_WIDTH;
    let pt = 16;
    preview.style.fontSize = pt * ptToPx + 'px';
    while (pt > 9 && preview.scrollHeight > preview.clientHeight + 1) {
      pt -= 0.5;
      preview.style.fontSize = pt * ptToPx + 'px';
    }
  }

  function render() {
    const msg = fields.message.value;
    pTo.textContent = fields.to.value.trim();
    pTo.hidden = !pTo.textContent;
    pFrom.textContent = fields.from.value.trim();
    pFrom.hidden = !pFrom.textContent;
    if (msg.trim()) {
      pMessage.textContent = msg;
      pMessage.classList.remove('cm-empty');
    } else {
      pMessage.textContent = placeholderText;
      pMessage.classList.add('cm-empty');
    }
    count.textContent = msg.length + ' / ' + MAX;
    count.classList.toggle('cm-over', msg.length >= MAX);
    fit();
  }

  // Next blank card in the same Greeting Card + Custom Message row (for Save & Next Card).
  function blankCards(exceptKey) {
    const row = pickerRow();
    if (!row) return [];
    return [...row.querySelectorAll('[data-card-picker-src] [data-card-msg-open]')].filter((b) => b.dataset.lineKey !== exceptKey && !(b.dataset.message || '').trim());
  }

  function open(btn, viaPicker = false) {
    const fromPicker = viaPicker || !!(picker && btn.closest('#card-picker-dialog'));
    if (fromPicker) picker.close();
    reopenPicker = fromPicker;
    current = { btn, fromPicker, card: btn.dataset.card || '', loose: btn.dataset.loose || '', forBox: btn.dataset.forBox || '', key: btn.dataset.lineKey, variantId: Number(btn.dataset.variantId), quantity: Number(btn.dataset.quantity) || 1 };
    current.next = fromPicker && blankCards(current.key).length > 0;
    // "Card 2 of 5" under the title when the message belongs to a multi-card row.
    const which = dialog.querySelector('[data-card-msg-which]');
    if (which) {
      const row = fromPicker ? pickerRow() : null;
      const cards = row ? [...row.querySelectorAll('[data-card-picker-src] [data-card-msg-open]')] : [];
      const index = cards.findIndex((b) => b.dataset.lineKey === current.key);
      which.hidden = index < 0;
      if (index >= 0) which.textContent = (which.dataset.template || 'Card %N of %T').replace('%N', index + 1).replace('%T', cards.length);
    }
    saveBtn.textContent = current.next ? (dialog.dataset.nextText || 'Save & Next Card') : (dialog.dataset.saveText || 'Save Message');
    fields.to.value = btn.dataset.to || '';
    fields.message.value = btn.dataset.message || '';
    fields.from.value = btn.dataset.from || '';
    errorEl.hidden = true;
    saveBtn.disabled = false;
    setFont(btn.dataset.font || 'Script');
    fillBoxes(current.key, btn.dataset.forBox || '');
    if (!dialog.open) dialog.showModal();
    render();
    requestAnimationFrame(() => { render(); fields.message.focus(); });
  }

  async function post(url, body) {
    const res = await fetch(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let data = {};
      try { data = await res.json(); } catch (e) { /* ignore */ }
      throw new Error(data.description || data.message || 'Could not save your message. Please try again.');
    }
    return res.json();
  }

  // Show the saved message + Edit button immediately, without waiting for the cart refresh.
  function markSaved(btn, properties, cartData) {
    if (!btn || !btn.isConnected) return;
    const newLine = (cartData && cartData.items || []).find((i) => i.properties && i.properties.Message === properties.Message && i.variant_id === current.variantId);
    if (newLine) btn.dataset.lineKey = newLine.key;
    btn.dataset.to = properties.To || '';
    btn.dataset.message = properties.Message || '';
    btn.dataset.from = properties.From || '';
    btn.dataset.font = properties.Font || 'Script';
    btn.dataset.forBox = properties['For Box'] || '';
    btn.classList.remove('cm-line-btn--write');
    btn.classList.add('cm-line-btn--edit');
    const svg = btn.querySelector('svg');
    btn.textContent = '';
    if (svg) btn.append(svg);
    btn.append(document.createTextNode(dialog.dataset.editText || 'Edit Message'));
    const wrap = btn.closest('[data-card-msg-line]');
    let snip = wrap && wrap.querySelector('[data-card-msg-snippet]');
    if (wrap && !snip) {
      snip = document.createElement('p');
      snip.className = 'cm-line-msg line-clamp-2';
      snip.setAttribute('data-card-msg-snippet', '');
      wrap.prepend(snip);
    }
    if (snip) {
      // Same rule as snippets/card-msg-preview.liquid: first two words of "To", else of the message.
      const two = (s) => { const w = String(s || '').trim().split(/\s+/).filter(Boolean); return w.slice(0, 2).join(' ') + (w.length > 2 ? '…' : ''); };
      const to = String(properties.To || '').trim();
      snip.textContent = to ? `${dialog.dataset.toLabel || 'To:'} ${two(to)}` : '\u201C' + two(properties.Message) + '\u201D';
    }
    if (!document.querySelector('[data-card-msg-open]:not([data-message]), [data-card-msg-open][data-message=""]')) {
      document.querySelectorAll('[data-card-msg-missing]').forEach((m) => m.remove());
    }
  }

  async function save() {
    const message = fields.message.value.trim();
    if (!message) {
      errorEl.textContent = dialog.dataset.requiredText || 'Please write your message.';
      errorEl.hidden = false;
      fields.message.focus();
      return;
    }
    const properties = { To: fields.to.value.trim(), Message: message, From: fields.from.value.trim(), Font: font, 'For Box': boxField && !boxField.hidden && boxSelect ? boxSelect.value : current.forBox };
    // Picker hidden (fewer than 2 boxes or cards): keep the set/box the card already belongs to (Oct 25 2026 fix).
    Object.keys(properties).forEach((k) => { if (!properties[k]) delete properties[k]; });
    if (current.card) properties._card = current.card; // keeps identical messages on separate lines
    if (current.loose && !properties['For Box']) properties._loose = current.loose; // a saved card stays unassigned
    saveBtn.disabled = true;
    errorEl.hidden = true;
    try {
      let cartData;
      if (current.quantity > 1) {
        await post('cart/change.js', { id: current.key, quantity: current.quantity - 1 });
        cartData = await post('cart/add.js', { items: [{ id: current.variantId, quantity: 1, properties }] });
        cartData = await (await fetch(root() + 'cart.js', { credentials: 'same-origin' })).json();
      } else {
        cartData = await post('cart/change.js', { id: current.key, quantity: 1, properties });
      }
      reopenPicker = false;
      dialog.close();
      if (current.quantity === 1) markSaved(current.btn, properties, cartData);
      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.();
      } catch (e) {
        if (current.quantity > 1) window.location.reload();
      }
      if (current.quantity > 1) {
        const stale = [...document.querySelectorAll('[data-card-msg-open]')].find((b) => b.dataset.lineKey === current.key && Number(b.dataset.quantity) === current.quantity);
        if (stale) window.location.reload();
      }
      // Save & Next Card: jump straight to the next unwritten card; otherwise back to the card list.
      if (current.fromPicker) {
        const nextCard = current.next ? blankCards(null)[0] : null;
        if (nextCard) open(nextCard, true);
        else showPicker();
      }
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
      saveBtn.disabled = false;
    }
  }

  // One Custom Message line per card: a line with quantity > 1 (e.g. 2 added at once, or + in the cart)
  // is split right away into separate quantity-1 lines, each with its own Write Your Message button.
  // The hidden _card property keeps the copies from merging back into one line.
  let splitting = false;
  const splitDone = new Set(); // line key + quantity already split, so a stale button never splits twice
  async function splitMultiples() {
    if (splitting || dialog.open) return;
    const seen = new Set();
    const multi = [...document.querySelectorAll('[data-card-msg-open]')].filter((b) => {
      const sig = b.dataset.lineKey + ":" + b.dataset.quantity;
      if (Number(b.dataset.quantity) <= 1 || seen.has(b.dataset.lineKey) || splitDone.has(sig)) return false;
      splitDone.add(sig);
      seen.add(b.dataset.lineKey);
      return true;
    });
    if (!multi.length) return;
    splitting = true;
    try {
      for (const b of multi) {
        const qty = Number(b.dataset.quantity);
        const variantId = Number(b.dataset.variantId);
        await post('cart/change.js', { id: b.dataset.lineKey, quantity: 1 });
        const stamp = Date.now().toString(36);
        const items = Array.from({ length: qty - 1 }, (_, i) => ({ id: variantId, quantity: 1, properties: { _card: `${stamp}-${i + 1}` } }));
        await post('cart/add.js', { items });
      }
      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.();
        const buttons = [...document.querySelectorAll('[data-card-msg-open]')];
        const stale = multi.some((b) => buttons.some((x) => x.dataset.lineKey === b.dataset.lineKey && x.dataset.quantity === b.dataset.quantity));
        if (stale) window.location.reload();
        else splitDone.clear();
      } catch (e) {
        window.location.reload();
      }
    } catch (e) {
      /* leave the cart as it is; the save flow still splits on first message */
    } finally {
      splitting = false;
    }
  }
  let splitTimer = null;
  const queueSplit = () => { clearTimeout(splitTimer); splitTimer = setTimeout(splitMultiples, 150); };
  new MutationObserver(queueSplit).observe(document.body, { childList: true, subtree: true });
  queueSplit();

  // Card picker (#card-picker-dialog): lists every card with a message so the shopper picks which one to write or edit.
  function pickerRow() {
    const rows = [...document.querySelectorAll('[data-card-group="combo"]')].filter((r) => r.querySelector('[data-card-picker-src]'));
    return rows.find((r) => r.dataset.cartId === pickerCartId && r.offsetParent !== null)
      || rows.find((r) => r.dataset.cartId === pickerCartId)
      || rows.find((r) => r.offsetParent !== null)
      || rows[0];
  }
  function showPicker(row) {
    if (!picker) return;
    row = row || pickerRow();
    if (!row) return;
    pickerCartId = row.dataset.cartId;
    picker.querySelector('[data-card-picker-list]').innerHTML = row.querySelector('[data-card-picker-src]').innerHTML;
    const source = row.querySelector('[data-card-group-progress]');
    const progress = picker.querySelector('[data-card-picker-progress]');
    progress.innerHTML = source ? source.innerHTML : '';
    progress.className = (source ? source.className.replace('cm-progress--split', '') : 'cm-progress') + ' mt-1';
    if (!picker.open) picker.showModal();
  }
  if (picker) {
    picker.querySelectorAll('[data-card-picker-close]').forEach((b) => b.addEventListener('click', () => picker.close()));
    picker.addEventListener('click', (e) => { if (e.target === picker) picker.close(); });
  }
  // Cancel / close in the message popup goes back to the picker it came from.
  dialog.addEventListener('close', () => { if (reopenPicker) { reopenPicker = false; showPicker(); } });

  // Greeting Card rows (snippets/cart-card-group.liquid): +/- and remove change the Greeting Card
  // line and its Custom Message lines together, so every card with a message keeps exactly one message.
  const parsePairs = (s) => (s || '').split(',').filter(Boolean).map((p) => {
    const i = p.lastIndexOf(':');
    return { key: p.slice(0, i), value: Number(p.slice(i + 1)) };
  });
  async function refreshCarts() {
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch (e) {
      window.location.reload();
    }
  }
  // Set a Greeting Card row to an exact count in one request: adds or removes cards, plus one
  // Custom Message per card on the combined row (blank messages are dropped first).
  async function setGroupQty(row, target) {
    const kind = row.dataset.cardGroup;
    const qty = Number(row.dataset.qty) || 0;
    const diff = target - qty;
    if (!diff) return;
    const gcVariant = Number(row.dataset.gcVariant);
    const cmVariant = Number(row.dataset.cmVariant);
    if (diff > 0) {
      const items = [{ id: gcVariant, quantity: diff }];
      if (kind === 'combo' && cmVariant) {
        const stamp = Date.now().toString(36);
        for (let i = 0; i < diff; i++) items.push({ id: cmVariant, quantity: 1, properties: { _card: `${stamp}-${i + 1}` } });
      }
      await post('cart/add.js', { items });
      return;
    }
    const count = -diff;
    const updates = {};
    let left = count;
    parsePairs(row.dataset.gcKeys).reverse().forEach((line) => {
      if (left <= 0) return;
      const take = Math.min(line.value, left);
      updates[line.key] = line.value - take;
      left -= take;
    });
    if (kind === 'combo') {
      const msgs = parsePairs(row.dataset.msgKeys).reverse();
      const ordered = [...msgs.filter((m) => m.value === 0), ...msgs.filter((m) => m.value === 1)];
      ordered.slice(0, count).forEach((m) => { updates[m.key] = 0; });
    }
    await post('cart/update.js', { updates });
  }

  // The number changes instantly; the cart saves once the shopper pauses (or leaves the box),
  // so tapping + five times is one save, not five. Taps during a save are kept and saved next.
  const groupTargets = {}; // "cartId|kind" -> wanted count
  const groupTimers = {};
  let groupSaving = false;
  const groupId = (row) => row.dataset.cartId + '|' + row.dataset.cardGroup;
  const findRow = (id) => [...document.querySelectorAll('[data-card-group]')].find((r) => groupId(r) === id);
  const writtenLost = (row, target) => row.dataset.cardGroup === 'combo' && target < Number(row.dataset.written);

  async function flushGroups() {
    if (groupSaving) return;
    const id = Object.keys(groupTargets)[0];
    if (!id) return;
    const target = groupTargets[id];
    delete groupTargets[id];
    const row = findRow(id);
    if (!row || target === Number(row.dataset.qty)) { flushGroups(); return; }
    groupSaving = true;
    row.style.opacity = '0.6';
    try {
      await setGroupQty(row, target);
    } catch (err) {
      /* the refresh below shows the real cart either way */
    }
    try {
      await refreshCarts();
    } finally {
      groupSaving = false;
      if (row.isConnected) row.style.opacity = '';
      // The refresh keeps old box values, so reset each count to the saved cart, then
      // re-apply counts tapped while saving onto the fresh row.
      document.querySelectorAll('[data-card-group-qty]').forEach((box) => { box.value = box.defaultValue; });
      Object.keys(groupTargets).forEach((key) => {
        const fresh = findRow(key);
        const input = fresh && fresh.querySelector('[data-card-group-qty]');
        if (input) input.value = groupTargets[key];
      });
      flushGroups();
    }
  }
  function queueGroup(row, target, delay) {
    const id = groupId(row);
    groupTargets[id] = target;
    const input = row.querySelector('[data-card-group-qty]');
    if (input) input.value = target;
    clearTimeout(groupTimers[id]);
    groupTimers[id] = setTimeout(flushGroups, delay);
  }
  const currentTarget = (row) => {
    const id = groupId(row);
    if (id in groupTargets) return groupTargets[id];
    const input = row.querySelector('[data-card-group-qty]');
    const typed = input ? parseInt(input.value, 10) : NaN;
    return Number.isFinite(typed) ? typed : Number(row.dataset.qty) || 0;
  };

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const opener = t.closest('[data-card-group-open]');
    if (opener) {
      // Write / Finish Messages start at the first unwritten card; Edit Messages opens the card list.
      e.preventDefault();
      const groupRow = opener.closest('[data-card-group]');
      pickerCartId = groupRow.dataset.cartId;
      const firstBlank = [...groupRow.querySelectorAll('[data-card-picker-src] [data-card-msg-open]')].find((b) => !(b.dataset.message || '').trim());
      if (firstBlank) open(firstBlank, true);
      else showPicker(groupRow);
      return;
    }
    const control = t.closest('[data-card-group-step], [data-card-group-remove]');
    if (!control) return;
    const row = control.closest('[data-card-group]');
    if (!row) return;
    e.preventDefault();
    if (control.hasAttribute('data-card-group-remove')) {
      // No prompt: removing cards just removes them.
      queueGroup(row, 0, 0);
      return;
    }
    const target = Math.max(0, currentTarget(row) + Number(control.dataset.cardGroupStep));
    // Stepping down removes cards without a prompt.
    queueGroup(row, target, 450);
  });

  // Remove a card that sits in a gift box (snippets/cart-box-card.liquid): drops its Custom Message line
  // and one Greeting Card, in one request.
  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-bb-card-remove]') : null;
    if (!btn) return;
    e.preventDefault();
    // No prompt: the card and its message are removed straight away.
    const card = btn.closest('[data-bb-card-in]');
    card?.setAttribute('aria-busy', 'true');
    const updates = { [btn.dataset.msgKey]: 0 };
    if (btn.dataset.gcKey) updates[btn.dataset.gcKey] = Math.max(0, (Number(btn.dataset.gcQty) || 1) - 1);
    try {
      await post('cart/update.js', { updates });
    } catch (err) {
      /* the refresh below shows the real cart either way */
    }
    await refreshCarts();
  });

  // Custom Message checkbox on the Greeting Card rows. Plain cards: checking adds one Custom Message
  // per card, so the row becomes "Greeting Card + Custom Message" with Write Message. Card + Message
  // row: unchecking drops its messages (asks first if any are written), so they become plain cards.
  document.addEventListener('click', async (e) => {
    const toggle = e.target instanceof Element ? e.target.closest('[data-card-group-msg-toggle]') : null;
    if (!toggle) return;
    e.preventDefault();
    const row = toggle.closest('[data-card-group]');
    if (!row || groupSaving || toggle.getAttribute('aria-busy') === 'true') return;
    const kind = row.dataset.cardGroup;
    const qty = Number(row.dataset.qty) || 0;
    const cmVariant = Number(row.dataset.cmVariant);
    if (!qty || (kind === 'plain' && !cmVariant)) return;
    if (kind === 'combo' && Number(row.dataset.written) > 0 && !window.confirm(toggle.dataset.confirm)) return;
    toggle.setAttribute('aria-checked', kind === 'combo' ? 'false' : 'true');
    toggle.setAttribute('aria-busy', 'true');
    groupSaving = true;
    row.style.opacity = '0.6';
    try {
      if (kind === 'plain') {
        const stamp = Date.now().toString(36);
        const items = [];
        for (let i = 0; i < qty; i++) items.push({ id: cmVariant, quantity: 1, properties: { _card: `${stamp}-${i + 1}` } });
        await post('cart/add.js', { items });
      } else {
        const updates = {};
        parsePairs(row.dataset.msgKeys).forEach((m) => { updates[m.key] = 0; });
        await post('cart/update.js', { updates });
      }
    } catch (err) {
      /* the refresh below shows the real cart either way */
    }
    try {
      await refreshCarts();
    } finally {
      groupSaving = false;
      if (row.isConnected) {
        row.style.opacity = '';
        toggle.removeAttribute('aria-busy');
      }
      flushGroups();
    }
  });

  // Typing a number: saved when they press Enter or leave the box.
  document.addEventListener('change', (e) => {
    const input = e.target instanceof Element ? e.target.closest('[data-card-group-qty]') : null;
    if (!input) return;
    const row = input.closest('[data-card-group]');
    if (!row) return;
    let target = parseInt(input.value, 10);
    if (!Number.isFinite(target) || target < 0) target = Number(row.dataset.qty) || 0;
    queueGroup(row, target, 0);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target instanceof Element && e.target.matches('[data-card-group-qty]')) {
      e.preventDefault();
      e.target.blur();
    }
  });

  function firstMissing() {
    return [...document.querySelectorAll('[data-card-msg-open]')].find((b) => !(b.dataset.message || '').trim() && b.offsetParent !== null)
      || [...document.querySelectorAll('[data-card-msg-open]')].find((b) => !(b.dataset.message || '').trim());
  }

  Object.values(fields).forEach((f) => f.addEventListener('input', render));
  fontButtons.forEach((b) => b.addEventListener('click', () => setFont(b.dataset.font)));
  form.addEventListener('submit', (e) => { e.preventDefault(); save(); });
  dialog.querySelectorAll('[data-card-msg-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  window.addEventListener('resize', () => { if (dialog.open) fit(); });
  if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', () => { if (dialog.open) fit(); });

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const btn = t.closest('[data-card-msg-open]');
    if (btn) { e.preventDefault(); open(btn); return; }
    const checkoutLink = t.closest('a[href$="/checkout"]');
    if (checkoutLink && document.querySelector('[data-card-msg-missing]')) {
      const missing = firstMissing();
      if (missing) { e.preventDefault(); e.stopImmediatePropagation(); open(missing); }
    }
  }, true);

  // Window capture runs before the Greeting Card matching guard (document capture), so a blank message is fixed first.
  window.addEventListener('submit', (e) => {
    const f = e.target;
    if (!(f instanceof HTMLFormElement)) return;
    if (e.submitter?.getAttribute('name') !== 'checkout') return;
    if (!f.querySelector('[data-card-msg-missing]')) return;
    const missing = firstMissing();
    if (!missing) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open(missing);
  }, true);
})();

/*
 * Gift box slot check (snippets/cart-box-slots.liquid): when the gift boxes have more slots than jars in
 * the cart, a checkout attempt shows the red notice instead of going to checkout. Never before an attempt.
 * Proceed Anyway lets this exact cart through. ANY cart change afterwards (add, remove, quantity, arranging)
 * resets both, so the next checkout attempt checks the updated cart again.
 */
(() => {
  const okKey = 'boxSlotsOk';
  const shownKey = 'boxSlotsShownSig';
  const store = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { if (v) sessionStorage.setItem(k, v); else sessionStorage.removeItem(k); } catch (e) { /* ignore */ } },
  };
  const html = document.documentElement;
  let lastForm = null;
  const sig = () => document.querySelector('[data-box-short]')?.dataset.boxSig || '';

  // After an attempt, the notice stays up only for that same cart (e.g. moving from the drawer to the cart page).
  function apply() {
    const shown = store.get(shownKey);
    html.classList.toggle('box-slots-attempted', !!shown && shown === sig());
  }
  function reset() {
    store.set(okKey, null);
    store.set(shownKey, null);
    html.classList.remove('box-slots-attempted');
  }

  // Any cart change made on the page resets the warning state.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await nativeFetch(...args);
    try {
      const u = String(args[0]?.url || args[0] || '');
      const method = String(args[1]?.method || args[0]?.method || 'GET').toUpperCase();
      if (method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u) && res.ok) reset();
    } catch (e) { /* ignore */ }
    return res;
  };

  window.addEventListener('submit', (e) => {
    const f = e.target;
    if (!(f instanceof HTMLFormElement)) return;
    if (e.submitter?.getAttribute('name') !== 'checkout') return;
    const marker = f.querySelector('[data-box-short]');
    if (!marker || store.get(okKey) === marker.dataset.boxSig) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    lastForm = f;
    store.set(shownKey, marker.dataset.boxSig);
    html.classList.add('box-slots-attempted');
    f.querySelector('[data-box-slots]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, true);

  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-box-proceed]') : null;
    if (!btn) return;
    const f = btn.closest('form') || lastForm;
    const marker = f?.querySelector('[data-box-short]');
    if (marker) store.set(okKey, marker.dataset.boxSig);
    store.set(shownKey, null);
    html.classList.remove('box-slots-attempted');
    const checkout = f?.querySelector('[name="checkout"]');
    if (f && checkout) f.requestSubmit(checkout);
  });

  apply();
  document.addEventListener('DOMContentLoaded', apply);
})();

(() => {
  // Build Your Own Box rows (snippets/cart-build-box-group.liquid). The trash button asks first (when the box has
  // jars): "Just the Box" removes the box and sends its jars back to the cart as single spices; "Box & Spices"
  // removes the box and all its jars. An empty box is removed straight away.
  const base = window.Shopify?.routes?.root || '/';
  async function removeBox(row, keys, mode) {
    row?.setAttribute('aria-busy', 'true');
    try {
      const returning = new Map();
      if (mode === 'box') {
        const cart = await (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
        (cart.items || []).forEach((it) => {
          if (it.properties?._bb === row?.dataset.bbGroup && it.properties?._slot != null) {
            returning.set(it.variant_id, (returning.get(it.variant_id) || 0) + it.quantity);
          }
        });
      }
      const updates = {};
      keys.forEach((k) => { updates[k] = 0; });
      const res = await fetch(`${base}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Could not remove the box. Please try again.');
      if (returning.size) {
        const back = await fetch(`${base}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [...returning].map(([id, quantity]) => ({ id, quantity })) }),
        });
        if (!back.ok) throw new Error((await back.json().catch(() => ({}))).description || 'The box was removed, but its spices could not be added back. Please add them again.');
      }
      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.();
      } catch (e2) {
        window.location.reload();
      }
    } catch (err) {
      row?.removeAttribute('aria-busy');
      window.alert(err.message);
    }
  }

  // A set's lines plus any card assigned to it ("For Box" = its label): a plain card line goes; a Custom Message
  // goes and takes one Greeting Card with it (the plain card line it was written on, which may hold several).
  // keepKeys: written Custom Message lines the shopper chose to save; they stay in the cart (with their Greeting Card)
  // as a loose card: "For Box" cleared and _loose set, so nothing re-assigns them until the shopper picks a box.
  async function removeSet(row, keys, label, keepKeys = [], dropKeys = []) {
    row?.setAttribute('aria-busy', 'true');
    try {
      const cart = await (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
      const items = cart.items || [];
      const keep = new Set(keepKeys);
      for (const it of items) {
        if (!keep.has(it.key)) continue;
        const properties = { ...(it.properties || {}), _loose: '1' };
        delete properties['For Box'];
        await fetch(`${base}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: it.quantity, properties }),
        });
      }
      const updates = {};
      keys.forEach((k) => { if (!keep.has(k)) updates[k] = 0; });
      let cardsToDrop = 0;
      if (label) {
        items.forEach((it) => {
          if (keep.has(it.key) || it.properties?.['For Box'] !== label) return;
          updates[it.key] = 0;
          if (it.handle === 'custom-message') cardsToDrop += it.quantity;
        });
      }
      // Written messages that sat in this box without naming it (the cart's only box) go too when not saved.
      items.forEach((it) => {
        if (!dropKeys.includes(it.key) || updates[it.key] === 0) return;
        updates[it.key] = 0;
        if (it.handle === 'custom-message') cardsToDrop += it.quantity;
      });
      if (cardsToDrop) {
        const plain = items.find((it) => it.handle === 'greeting-card' && !it.properties?.['For Box'] && updates[it.key] === undefined);
        if (plain) updates[plain.key] = Math.max(0, plain.quantity - cardsToDrop);
      }
      const res = await fetch(`${base}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Could not remove the set. Please try again.');
      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.();
      } catch (e2) {
        window.location.reload();
      }
    } catch (err) {
      row?.removeAttribute('aria-busy');
      window.alert(err.message);
    }
  }

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const btn = t.closest('[data-bb-group-remove]');
    if (btn) {
      e.preventDefault();
      const row = btn.closest('[data-bb-group]');
      const keys = (btn.dataset.bbGroupRemove || '').split(',').filter(Boolean);
      if (!keys.length) return;
      // Every box/set (merchant, Oct 25 2026): "This will delete Box N and all of its contents. Proceed?" Yes / No.
      // Yes removes the box, its spices and its card + message.
      const choice = row?.querySelector('[data-bb-remove-choice]');
      if (!choice) { removeSet(row, keys, btn.dataset.bbSetRemove || ''); return; }
      // A written card message in the box: first "Would you like to save your greeting card message?" (merchant, Oct 25 2026).
      const save = row.querySelector('[data-bb-save-msg]');
      const open = !choice.hidden || (save && !save.hidden);
      choice.hidden = true;
      if (save) save.hidden = true;
      delete row.dataset.keepMsg;
      if (open) return;
      const first = save || choice;
      first.hidden = false;
      first.querySelector('button')?.focus();
      return;
    }
    const savePick = t.closest('[data-bb-save-msg-pick]');
    if (savePick) {
      e.preventDefault();
      const row = savePick.closest('[data-bb-group]');
      if (!row) return;
      row.dataset.keepMsg = savePick.dataset.bbSaveMsgPick;
      savePick.closest('[data-bb-save-msg]').hidden = true;
      const choice = row.querySelector('[data-bb-remove-choice]');
      if (choice) { choice.hidden = false; choice.querySelector('[data-bb-remove-yes]')?.focus(); }
      return;
    }
    const yes = t.closest('[data-bb-remove-yes]');
    if (yes) {
      e.preventDefault();
      const row = yes.closest('[data-bb-group]');
      const rbtn = row?.querySelector('[data-bb-group-remove]');
      const keys = (rbtn?.dataset.bbGroupRemove || '').split(',').filter(Boolean);
      const written = (row?.querySelector('[data-bb-save-msg]')?.dataset.bbSaveMsg || '').split(',').filter(Boolean);
      const keep = row?.dataset.keepMsg === '1' ? written : [];
      const drop = row?.dataset.keepMsg === '1' ? [] : written;
      if (keys.length) removeSet(row, keys, rbtn.dataset.bbSetRemove || '', keep, drop);
      return;
    }
    // Box price "Details" popup: every item in the box with its price.
    const dOpen = t.closest('[data-bb-details-open]');
    if (dOpen) {
      e.preventDefault();
      // Dropdown under the price (Oct 26 2026): toggles open/closed.
      const panel = dOpen.closest('[data-bb-group]')?.querySelector('[data-bb-details-dialog]');
      if (panel) {
        const open = panel.hidden;
        panel.hidden = !open;
        dOpen.setAttribute('aria-expanded', String(open));
      }
      return;
    }
    const pick = t.closest('[data-bb-remove-mode]');
    if (pick) {
      e.preventDefault();
      const row = pick.closest('[data-bb-group]');
      const keys = (row?.querySelector('[data-bb-group-remove]')?.dataset.bbGroupRemove || '').split(',').filter(Boolean);
      if (keys.length) removeBox(row, keys, pick.dataset.bbRemoveMode);
      return;
    }
    const cancel = t.closest('[data-bb-remove-cancel]');
    if (cancel) {
      e.preventDefault();
      const choice = cancel.closest('[data-bb-remove-choice]');
      if (choice) choice.hidden = true;
      const crow = cancel.closest("[data-bb-group]");
      if (crow) delete crow.dataset.keepMsg;
    }
  });
})();

/*
 * "Goes in:" box picker on each written card in the cart (snippets/card-message-line.liquid). Saves the choice as
 * the card's "For Box" property, keeping its other properties (To, Message, From, Font, _card).
 */
(() => {
  const base = window.Shopify?.routes?.root || '/';
  document.addEventListener('change', async (e) => {
    const select = e.target instanceof HTMLSelectElement && e.target.matches('[data-card-box-select]') ? e.target : null;
    if (!select) return;
    const wrap = select.closest('.cm-box-pick');
    wrap?.setAttribute('aria-busy', 'true');
    try {
      const cart = await (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
      const line = (cart.items || []).find((it) => it.key === select.dataset.cardBoxSelect);
      if (!line) throw new Error('This card changed in another tab. Please refresh the page.');
      const properties = { ...line.properties };
      if (select.value) properties['For Box'] = select.value; else delete properties['For Box'];
      const res = await fetch(`${base}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Could not save the box. Please try again.');
      try {
        await customElements.whenDefined('s-cart');
        await customElements.get('s-cart')?.refreshMountedCarts?.();
      } catch (e2) {
        window.location.reload();
      }
    } catch (err) {
      wrap?.removeAttribute('aria-busy');
      window.alert(err.message);
    }
  });
})();

/*
 * Pre-made set + gift box pairing (merchant, Oct 25 2026). When the cart has a set and a plain gift box of the
 * same size (window.WZ_SET_SIZES, from layout/theme.liquid), one set and one box are tagged with the same hidden
 * _sg id and a visible "Gift Set" name (e.g. "I Love Steak Gift Box"), qty 1 each, so the cart shows them as one
 * row (snippets/cart-set-group.liquid), cards can go in that box, and orders show they belong together.
 * A tagged line whose partner is gone is untagged so it can pair again. Runs on page load and after any cart change.
 * Also: the set group's trash button ([data-sg-remove]) removes the set and its box.
 */
(() => {
  const base = window.Shopify?.routes?.root || '/';
  const SIZES = window.WZ_SET_SIZES || {};
  const boxSize = (it) => (it.handle === 'small-gift-box' ? 3 : it.handle === 'large-gift-box' ? 6 : 0);
  const setSize = (it) => SIZES[String(it.product_id)] || 0;
  const nativeFetch = window.fetch.bind(window);
  let selfCalls = 0;
  const post = async (url, body) => {
    selfCalls++;
    try {
      const r = await nativeFetch(base + url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('cart');
      return r.json();
    } finally { selfCalls--; }
  };
  const clean = (props) => { const p = { ...props }; Object.keys(p).forEach((k) => { if (p[k] == null) delete p[k]; }); return p; };
  // Give ONE unit of a line new properties (splits it off when the line has more than one).
  async function tag(it, props) {
    const properties = clean({ ...(it.properties || {}), ...props });
    if (it.quantity === 1) return post('cart/change.js', { id: it.key, quantity: 1, properties });
    await post('cart/change.js', { id: it.key, quantity: it.quantity - 1 });
    return post('cart/add.js', { items: [{ id: it.variant_id, quantity: 1, properties }] });
  }
  // One change per call; returns true when something changed (caller loops).
  async function step() {
    const cart = await (await nativeFetch(base + 'cart.js', { credentials: 'same-origin' })).json();
    const items = cart.items || [];
    const groups = {};
    items.forEach((it) => { const g = it.properties?._sg; if (g) (groups[g] = groups[g] || []).push(it); });
    for (const list of Object.values(groups)) {
      // Valid: exactly one set (qty 1) plus at most one gift box (qty 1). A set on its own is a valid group too
      // (it may carry a Greeting Card); a gift box can join it later.
      const sets = list.filter((i) => setSize(i));
      const boxes = list.filter((i) => boxSize(i));
      const ok = sets.length === 1 && boxes.length <= 1 && sets.length + boxes.length === list.length && list.every((i) => i.quantity === 1) && (!boxes[0] || boxSize(boxes[0]) === setSize(sets[0]));
      if (!ok) { await tag(list.find((i) => i.quantity > 1) || list[list.length - 1], { _sg: null, 'Gift Set': null }); return true; }
    }
    const used = new Set(items.map((i) => i.properties?.['Gift Set']).filter(Boolean));
    const freeBox = (n) => items.find((b) => boxSize(b) === n && !b.properties?._bb && !b.properties?._sg && b.properties?._slot == null);
    // 1) A set already in a group without a box takes a free box of its size (its name and card stay).
    for (const list of Object.values(groups)) {
      if (list.some((i) => boxSize(i))) continue;
      const set = list[0];
      const box = freeBox(setSize(set));
      if (!box) continue;
      await tag(box, { _sg: set.properties._sg, 'Gift Set': set.properties['Gift Set'] });
      return true;
    }
    // 2) A loose set and a free box of its size become a new group.
    for (const set of items) {
      const n = setSize(set);
      if (!n || set.properties?._sg) continue;
      const box = freeBox(n);
      if (!box) continue;
      const id = 'sg' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const title = set.product_title || '';
      const name = /\bset$/i.test(title) ? title : `${title} Set`;
      let label = name;
      for (let k = 2; used.has(label); k++) label = `${name} ${k}`;
      await tag(box, { _sg: id, 'Gift Set': label });
      await tag(set, { _sg: id, 'Gift Set': label });
      return true;
    }
    return false;
  }
  const refresh = async () => {
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch (e) { /* the next page load shows it */ }
  };
  let running = false;
  let again = false;
  async function run() {
    if (running) { again = true; return; }
    running = true;
    let changed = false;
    try {
      for (let i = 0; i < 10; i++) { if (!(await step())) break; changed = true; }
    } catch (e) { /* leave the cart as it is */ }
    running = false;
    if (changed) await refresh();
    if (again) { again = false; run(); }
  }
  let timer = 0;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(run, 250); };
  window.fetch = function (input, init) {
    const p = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const method = (init?.method || input?.method || 'GET').toUpperCase();
      if (!selfCalls && method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) p.then(schedule, () => {});
    } catch (e) { /* ignore */ }
    return p;
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule); else schedule();
  window.addEventListener('pageshow', (e) => { if (e.persisted) schedule(); });

  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-sg-remove]') : null;
    if (!btn) return;
    e.preventDefault();
    const keys = (btn.dataset.sgRemove || '').split(',').filter(Boolean);
    if (!keys.length) return;
    const row = btn.closest('[data-sg-group]');
    row?.setAttribute('aria-busy', 'true');
    try {
      const updates = {};
      keys.forEach((k) => { updates[k] = 0; });
      await post('cart/update.js', { updates });
      await refresh();
    } catch (err) {
      row?.removeAttribute('aria-busy');
      window.location.reload();
    }
  });
})();
